import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate("/admin/login"); return; }

    const sessionId = match[1];
    axios.post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true })
      .then(res => {
        localStorage.setItem("lf_session", res.data.session_token);
        navigate("/admin/dashboard", { state: { user: res.data.user }, replace: true });
      })
      .catch((e) => {
        // Surface 403 / rejection reasons so admins can fix their setup
        // instead of silently bouncing back to /admin/login forever.
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || e?.message || "Sign-in failed.";
        setError({ status, detail });
      });
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="auth-callback-error">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-2xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-red-50 rounded-full mb-4">
            <AlertTriangle size={22} className="text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-zinc-950 mb-2">Sign-in blocked</h1>
          <p className="text-sm text-zinc-700 leading-relaxed mb-5" data-testid="auth-callback-reason">{error.detail}</p>
          <div className="text-xs text-zinc-500 mb-5 leading-relaxed">
            {error.status === 403
              ? "Tip: ask another admin to sign in with email + password and add your Google email to Admin → Settings → Allowed Admin Emails (CSV)."
              : error.status === 502 || error.status === 504 || error.status === 520 || !error.status
              ? "This looks like a temporary upstream problem. Please wait a few seconds and try Google sign-in again, or fall back to the email + password form below."
              : `HTTP ${error.status || "—"}. If this persists, try the email + password sign-in below.`}
          </div>
          <Link to="/admin/login" replace data-testid="auth-callback-back-to-login"
            className="inline-block px-5 py-2.5 bg-zinc-950 text-white text-sm font-semibold rounded-full hover:bg-zinc-800">
            Back to admin login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <Loader2 size={36} className="text-zinc-400 animate-spin mx-auto mb-4" />
        <p className="text-zinc-600 text-sm">Completing sign in...</p>
      </div>
    </div>
  );
}
